import { useCallback } from 'react';
import {
  TravellerWithCollectionMembershipsDTO,
  useDeleteAllpayUserHook,
  useDeleteCardCreditBySerialHook,
  useListAllpayUsersHook,
  useListCardCreditsHook,
} from '@/api/trafikportalen';

export interface PartialDeleteTravellerProductsResult {
  deleted: {
    allpayUsers: number;
    cardCredits: number;
  };
  failedCount: number;
}

export class PartialDeleteTravellerProductsError extends Error {
  readonly result: PartialDeleteTravellerProductsResult;

  constructor(result: PartialDeleteTravellerProductsResult) {
    super('Partial delete of traveller associated products');
    this.name = 'PartialDeleteTravellerProductsError';
    this.result = result;
  }
}

export const isPartialDeleteTravellerProductsError = (
  error: unknown,
): error is PartialDeleteTravellerProductsError =>
  error instanceof PartialDeleteTravellerProductsError;

export const useDeleteTravellerAssociatedProducts = () => {
  const listAllpayUsers = useListAllpayUsersHook();
  const listCardCredits = useListCardCreditsHook();
  const deleteAllpayUser = useDeleteAllpayUserHook();
  const deleteCardCreditBySerial = useDeleteCardCreditBySerialHook();

  const deleteAssociatedProducts = useCallback(
    async (traveller: TravellerWithCollectionMembershipsDTO) => {
      const {
        memberships,
        traveller: { id: travellerId, bobcat_alltix_id },
      } = traveller;
      const collectionMemberIds = new Set(
        memberships.map((membership) => membership.collection_member_id),
      );
      const departmentIds = [
        ...new Set(memberships.map((membership) => membership.department_id)),
      ];

      const [allpayUsers, cardCreditsByDepartment] = await Promise.all([
        listAllpayUsers({ traveller_id: travellerId }),
        Promise.all(
          departmentIds.map((departmentId) =>
            listCardCredits({ department_id: departmentId }),
          ),
        ),
      ]);

      const cardCredits = [
        ...new Map(
          cardCreditsByDepartment
            .flat()
            .map((cardCredit) => [cardCredit.card_serial, cardCredit]),
        ).values(),
      ];

      const allpayToDelete = allpayUsers ?? [];
      const creditsToDelete = cardCredits.filter(
        (card) =>
          (card.collection_member_id &&
            collectionMemberIds.has(card.collection_member_id)) ||
          (!!bobcat_alltix_id && card.BobcatTravellerID === bobcat_alltix_id),
      );

      const [allpayResults, creditResults] = await Promise.all([
        Promise.allSettled(
          allpayToDelete.map((user) => deleteAllpayUser(user.id)),
        ),
        Promise.allSettled(
          creditsToDelete.map((card) =>
            deleteCardCreditBySerial(card.card_serial),
          ),
        ),
      ]);

      const countFulfilled = (results: PromiseSettledResult<unknown>[]) =>
        results.filter((result) => result.status === 'fulfilled').length;
      const countRejected = (results: PromiseSettledResult<unknown>[]) =>
        results.filter((result) => result.status === 'rejected').length;

      const deleted = {
        allpayUsers: countFulfilled(allpayResults),
        cardCredits: countFulfilled(creditResults),
      };
      const failedCount =
        countRejected(allpayResults) + countRejected(creditResults);

      if (failedCount > 0) {
        throw new PartialDeleteTravellerProductsError({
          deleted,
          failedCount,
        });
      }
    },
    [
      deleteAllpayUser,
      deleteCardCreditBySerial,
      listAllpayUsers,
      listCardCredits,
    ],
  );

  return { deleteAssociatedProducts };
};
